import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Form, Spinner, Alert, Row, Col,
  InputGroup
} from 'react-bootstrap';
import { FaTrashAlt, FaInfoCircle, FaEdit, FaKey, FaSearch } from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useLocation, useNavigate } from "react-router-dom";
import { usersAPI } from '../../api/apiClient';
import PaginationComponent from '../common/PaginationComponent';
import { useTranslation } from 'react-i18next';
import '../../assets/css/Settings.css';

function Customers() {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDelete, setShowDelete] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const navigate = useNavigate();
  const location = useLocation();

  const highlightedUserId = location.state?.scrollToUser;


  // استرجاع رقم الصفحة من state التنقل
  useEffect(() => {
    if (location.state?.fromPage) {
      fetchUsers(location.state.fromPage);
    }
  }, [location.state]);


  // Fetch users with pagination and search
  const fetchUsers = async (page = 1, search = searchTerm) => {
    try {
      setLoading(true);
      setError('');
      const response = await usersAPI.index({
        page,
        per_page: pagination.per_page,
        search,
      });

      if (response.success) {
        const fetchedData = response.data.data || response.data;
        setData(fetchedData);
        setPagination(response.data.meta || {
          current_page: page,
          last_page: response.data.last_page || 1,
          per_page: response.data.per_page || 10,
          total: response.data.total || 0,
        });
      } else {
        setError(response.message || t('failed_to_fetch_data'));
      }
    } catch (err) {
      setError(t('failed_to_fetch_data'));
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  // Debounced Search Effect
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm !== undefined) {
        fetchUsers(1, searchTerm);
      }
    }, 1500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  useEffect(() => {
    if (!location.state?.fromPage) {
      fetchUsers();
    }
  }, []);

  useEffect(() => {
    if (highlightedUserId) {
      const element = document.getElementById(`user-${highlightedUserId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [data]);


  // Handle user deletion
  const confirmDelete = async () => {
    try {
      setLoading(true);
      const response = await usersAPI.delete(selectedUser.id);
      
      if (response.success) {
        setShowDelete(false);
        fetchUsers(pagination.current_page);
        alert(t('user_deleted_successfully'));
      } else {
        alert(response.message || t('failed_to_delete_user'));
      }
    } catch (err) {
      alert(t('failed_to_delete_user'));
      console.error('Error deleting user:', err);
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  if (loading) {
    return (
      <div className="container py-4 text-center">
        <Spinner animation="border" />
        <p className="mt-2">{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="py-4">
      <div className="d-flex justify-content-between align-items-center flex-wrap">
        <h3 className="fw-bold mb-3">👤 {t('users_list')}</h3>

        {/* Search Bar */}
        <Row className="mb-3">
          <Col>
            <InputGroup>
              <InputGroup.Text><FaSearch /></InputGroup.Text>
              <Form.Control
                type="text"
                placeholder={t('search_by_email')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <Button 
                  variant="outline-secondary" 
                  onClick={clearSearch}
                  title={t("reset_search")}
                >
                  ✕
                </Button>
              )}
            </InputGroup>
          </Col>
        </Row>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="table-container shadow-sm rounded">
        <div className='table-responsive'>
          <Table bordered hover className="custom-table mb-0 text-center">
            <thead className="table-dark">
              <tr>
                <th>#</th>
                <th>{t('first_name')}</th>
                <th>{t('company_name')}</th>
                <th>{t('last_name')}</th>
                <th>{t('Email')}</th>
                <th>{t('Address')}</th>
                <th>{t('postal_code')}</th>
                <th>{t('employees')}</th>
                <th>{t('action')}</th>
              </tr>
            </thead>
            <tbody>
              {data.map((user, idx) => (
                <tr key={user.id} id={`user-${user.id}`} className={user.id === highlightedUserId ? "user-highlight" : ""}>

                  <td>{(pagination.current_page - 1) * pagination.per_page + idx + 1}</td>
                  <td>{user.firstname || '-'}</td>
                  <td>{user.company_name || '-'}</td>
                  <td>{user.lastname || '-'}</td>
                  <td>
                    {/* {user.email} */}
                    {searchTerm ? (
                      <span 
                        dangerouslySetInnerHTML={{
                          __html: user.email?.replace(
                            new RegExp(searchTerm, 'gi'), 
                            match => `<mark class="bg-warning">${match}</mark>`
                          )
                        }} 
                      />
                    ) : (
                      user.email
                    )}
                  </td>
                  <td>{user.street_address || user.addressone || '-'}</td>
                  <td>{user.postal_code || '-'}</td>
                  <td>{user.employees_count || '-'}</td>

                  {/* Actions */}
                  <td>
                    <Button
                      variant="outline-info"
                      className="me-1 mb-1"
                       onClick={() => navigate(`/admin/customers/details/${user.id}`, { 
                        state: { 
                          fromPage: pagination.current_page,
                          userId: user.id // إرسال معرف المستخدم لتحديده
                        } 
                      })}
                      title={t('view_details')}
                    >
                      <FaInfoCircle />
                    </Button>
                    <Button
                      variant="outline-danger"
                      className="mb-1"
                      onClick={() => {
                        setSelectedUser(user);
                        setShowDelete(true);
                      }}
                      title={t('Delete')}
                    >
                      <FaTrashAlt />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>

      {pagination.total > 0 && (
        <PaginationComponent
          currentPage={pagination.current_page}
          lastPage={pagination.last_page}
          onPageChange={fetchUsers}
          disabled={loading}
        />
      )}

      {/* Delete Modal */}
      <Modal show={showDelete} onHide={() => setShowDelete(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('confirm_deletion')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('confirm_delete_user')} <strong>{selectedUser?.firstname} {selectedUser?.lastname}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDelete(false)} disabled={loading}>
            {t('Cancel')}
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={loading}>
            {loading ? t('Deleting...') : t('Delete')}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .postal-codes-container {
          border: 1px solid #dee2e6;
          border-radius: 0.375rem;
          padding: 1rem;
          background-color: #f8f9fa;
        }
        
        mark {
          background-color: #ffc107;
          padding: 0.1rem 0.2rem;
          border-radius: 0.2rem;
        }
        
        .user-highlight {
          background-color: #d4edda !important;
          border-left: 4px solid #28a745;
          transition: all 0.3s ease;
        }
        
        .user-highlight td {
          font-weight: 600;
          color: #028d22ff;
        }
      `}</style>
    </div>
  );
}

export default Customers;